import { DatabaseId } from "@syla/shared/types/helpers/Id";
import { StripeBillingPortalAction } from "@syla/shared/types/requests/CreateStripeBillingPortalRequest";
import { getSubscriptionState } from "@syla/shared/types/subscriptions/getSubscriptionState";
import {
  Lifecycle,
  SubscriptionType,
} from "@syla/shared/types/subscriptions/subscriptionState";
import { createStripeBillingPortalSession } from "../api/plans/createStripeBillingPortalSession";
import { createSubscription } from "../api/plans/createSubscription";
import { captureRequestError } from "../helper/captureRequestError";
import { redirectTo } from "../helper/RedirectTo";
import { Subscription } from "../types/plans/subscription";

/**
 * Opens a checkout session with the selected plan
 */
export const stripePurchaseOrUpgrade = async ({
  accountId,
  cancelUrl,
  financialYear,
  plan,
  successUrl,
  currentSubscription,
}: {
  accountId: string;
  successUrl: string;
  plan: { _id: DatabaseId };
  financialYear?: string;
  cancelUrl?: string;
  currentSubscription?: Subscription;
}) => {
  let redirectUrl: string;
  try {
    // if there is no subscription, then the button creates a new subscription checkout
    if (!currentSubscription) {
      const response = await createSubscription(accountId, {
        planId: plan._id,
        successUrl: successUrl,
        cancelUrl: cancelUrl ?? successUrl,
        financialYear,
      });
      redirectUrl = response.checkoutUrl;
    }

    // if there is an existing subscription
    else {
      const state = getSubscriptionState(currentSubscription);

      // if it's in a terminated state, or it's a manual plan, then we also
      // create a new subscription checkout
      if (
        state.lifecycle === Lifecycle.Terminated ||
        state.type === SubscriptionType.Manual
      ) {
        const response = await createSubscription(accountId, {
          planId: plan._id,
          successUrl: successUrl,
          cancelUrl: cancelUrl ?? successUrl,
          financialYear,
        });
        redirectUrl = response.checkoutUrl;
      }

      // otherwise, the user must be trying to update a stripe subscription
      else if (currentSubscription.stripeSubscriptionId) {
        if (plan._id === currentSubscription.plan._id) {
          // to allow for plan renewal
          const response = await createStripeBillingPortalSession(accountId, {
            action: StripeBillingPortalAction.MainPortal,
            returnUrl: successUrl,
          });
          redirectUrl = response.portalUrl;
        } else {
          const response = await createStripeBillingPortalSession(accountId, {
            action: StripeBillingPortalAction.UpdateSubscription,
            stripeSubscriptionId: currentSubscription.stripeSubscriptionId,
            returnUrl: successUrl,
          });
          redirectUrl = response.portalUrl;
        }
      } else {
        // this shouldn't be possible
        return;
      }
    }
  } catch (error) {
    captureRequestError(error);
    throw error;
  }

  // navigate to complete checkout
  redirectTo(redirectUrl);
};
